// The session (Console D `#/sessions/:id`, APS-315): the head with the session's finding, then
// the session as an orbit around the device with its events and their links, the main thread and
// the network on one axis, the spans and the logs; at the side the replay when there is one
// (the wireframe player, plan step 3.3), the session's issues (APS-301), its resource attributes
// and its feature flags. Picking an event moves the replay and the cursors to its moment.
import { useQuery } from '@tanstack/react-query'
import { Link, useParams, useSearch } from '@tanstack/react-router'
import { type CSSProperties, Fragment, type ReactNode, useState } from 'react'
import { instrumentationQuery, orgQuery, sessionQuery } from '../api/queries'
import type { SessionTimeline } from '../api/types'
import { DeviceData } from '../components/DeviceData'
import {
  Btn,
  btnClass,
  copyText,
  EmptyNote,
  HovRow,
  Kind,
  LK,
  PageHead,
  Pill,
  Section,
  Table,
  TableFoot,
  Tag,
  useToast,
} from '../components/d'
import { Orbit } from '../components/Orbit'
import {
  ReplayBoundary,
  type ReplayMarker,
  ReplayPlayer,
  useReplay,
} from '../components/ReplayPlayer'
import { ErrorState, Loading } from '../components/states'
import { type MessageId, type Translator, useI18n } from '../i18n'
import { FLAG_ATTR_PREFIX, OUTCOME, RESOURCE } from '../lib/conventions'
import { duration, offset, shortId } from '../lib/format'
import { orbitFractions, type SessionModel, sessionModel, type TimelineEvent } from '../lib/session'
import { type Severity, sessionLogs, sessionTraces, type Trace } from '../lib/sessionDetail'
import {
  type EventLink,
  eventLinks,
  finding,
  headIssue,
  LOG_WINDOW,
  logIssue,
  logWindow,
} from '../lib/sessionLinks'
import { sandboxFor } from '../sandbox'
import { useProjectParams } from './params'
import { triggerLabel } from './SignalsRule'
import { carried, type ScopeSearch } from './search'

type Issue = SessionTimeline['issues'][number]

export function valueText(i18n: Translator, e: TimelineEvent): string {
  if (e.valueKind === 'duration') return e.duration > 0 ? duration(i18n, e.duration) : ''
  if (e.valueKind === 'status') return e.value
  if (e.value === 'crash') return i18n.t('session.signal.crash')
  if (e.value === 'error') return i18n.t('session.signal.error')
  if (e.value === 'exit') return i18n.t('session.signal.exit')
  return e.value
}

export const pillTone = (e: TimelineEvent) =>
  e.tone === 'crash' ? ('crash' as const) : e.tone === 'warn' ? ('warn' as const) : undefined

/** A share of the session's length, as a CSS position on its one time axis. */
const at = (ms: number, total: number) =>
  `${Math.min(100, Math.max(0, (ms / Math.max(1, total)) * 100))}%`

/** An event's links as the mock's small mono tags (`.lk`). */
function Links({ links }: { links: EventLink[] }) {
  const { t } = useI18n()
  const { orgId, projectId } = useProjectParams()
  const search = useSearch({ strict: false }) as ScopeSearch
  const keep = carried(search)
  const params = { orgId, projectId }
  return (
    <>
      {links.map((l) => {
        switch (l.kind) {
          case 'start':
            return (
              <Link
                key="start"
                className={LK}
                to="/o/$orgId/p/$projectId/performance/start"
                params={params}
                search={keep}
              >
                {t('session.link.start')}
              </Link>
            )
          case 'screen':
            return (
              <Link
                key={`screen:${l.screen}`}
                className={LK}
                to="/o/$orgId/p/$projectId/performance/screens/detail"
                params={params}
                search={{ ...keep, screen: l.screen }}
                translate="no"
              >
                {t('session.link.screen', { screen: l.screen })}
              </Link>
            )
          case 'trace':
            return (
              <Link
                key={`trace:${l.traceId}`}
                className={LK}
                to="/o/$orgId/p/$projectId/traces/$traceId"
                params={{ ...params, traceId: l.traceId }}
                search={keep}
                translate="no"
              >
                {t('session.link.trace', { id: l.traceId.slice(0, 4) })}
              </Link>
            )
          case 'endpoint':
            return (
              <Link
                key="endpoint"
                className={LK}
                to="/o/$orgId/p/$projectId/performance/network/endpoint"
                params={params}
                search={{ ...keep, method: l.method, host: l.host, template: l.template }}
              >
                {t('session.link.endpoint')}
              </Link>
            )
          case 'issue':
            return (
              <Link
                key={`issue:${l.id}`}
                className={LK}
                to="/o/$orgId/p/$projectId/issues/$issueId"
                params={{ ...params, issueId: l.id }}
                search={keep}
                translate="no"
              >
                {shortId(l.id, 8, 4)}
              </Link>
            )
        }
        return null
      })}
    </>
  )
}

/** The head's first meta line: the finding, then the counts. */
function findingText(i18n: Translator, model: SessionModel, errors: number): ReactNode {
  const { t } = i18n
  const f = finding(model)
  const counts = t('session.counts', {
    screens: model.screens,
    requests: model.requests,
    failed: model.failedRequests,
    errors,
  })
  if (model.outcome !== OUTCOME.crashed) {
    const word: Record<string, MessageId> = {
      [OUTCOME.abnormalExit]: 'session.finding.abnormal',
      [OUTCOME.active]: 'session.finding.active',
    }
    return (
      <>
        {t(word[model.outcome] ?? 'session.finding.exited', {
          after: duration(i18n, model.length),
        })}
        {` · ${counts}`}
      </>
    )
  }
  return (
    <>
      <span className="c-crash">{t('session.finding.crashed', { at: duration(i18n, f.at) })}</span>
      {f.before ? (
        <span translate="no">
          {`, ${t(f.before.status ? 'session.finding.beforeStatus' : 'session.finding.before', {
            gap: duration(i18n, f.before.gap),
            name: f.before.name,
            status: f.before.status,
          })}`}
        </span>
      ) : null}
      {` · ${counts}`}
    </>
  )
}

/** Main thread and network lanes on the session's axis, with the picked moment's cursor. */
function Lanes({ model, cursor }: { model: SessionModel; cursor: number }) {
  const i18n = useI18n()
  const { t } = i18n
  const total = Math.max(1, model.length)
  const main = model.events.filter(
    (e) =>
      e.signal === 'start' || e.signal === 'screen' || e.signal === 'span' || e.signal === 'hang',
  )
  const net = model.events.filter((e) => e.signal === 'network')
  const crash = model.events.find((e) => e.signal === 'crash')
  const bar = (e: TimelineEvent, cls?: string) => (
    <i
      key={e.id}
      className={cls}
      style={{ left: at(e.at, total), width: `max(2px, ${at(e.duration, total)})` }}
      title={[e.name, e.sub, duration(i18n, e.duration)].filter(Boolean).join(' · ')}
    />
  )
  return (
    <div className="ss-lanes" translate="no">
      <div className="ss-lane">
        <span>{t('session.lane.main')}</span>
        <div className="ss-ln">
          {main.map((e) => bar(e, e.signal === 'hang' ? 'hang' : undefined))}
          {crash ? (
            <span className="ss-end" style={{ left: at(crash.at, total) }} title={crash.name} />
          ) : null}
          <span className="ss-cur" style={{ left: at(cursor, total) }} />
        </div>
      </div>
      <div className="ss-lane">
        <span>{t('session.lane.network')}</span>
        <div className="ss-ln">
          {net.map((e) => bar(e, e.tone === 'crash' ? 'fail' : undefined))}
          <span className="ss-cur" style={{ left: at(cursor, total) }} />
        </div>
      </div>
      <div className="ss-laxis">
        <span />
        <div>
          <span>{duration(i18n, 0)}</span>
          <span>{duration(i18n, total / 2)}</span>
          <span className={crash ? 'c-crash' : undefined}>
            {crash
              ? t('session.lane.crashAt', { at: duration(i18n, crash.at) })
              : duration(i18n, total)}
          </span>
        </div>
      </div>
    </div>
  )
}

/** Whole seconds along the axis, at most six of them (every 1, 2, 5 or 10 s, as fits). */
export function axisTicks(total: number): number[] {
  const step = [1000, 2000, 5000, 10_000, 30_000, 60_000].find((s) => total / s <= 6) ?? 300_000
  const ticks: number[] = []
  for (let ms = 0; ms <= total; ms += step) ticks.push(ms)
  return ticks
}

/** Spans deeper than this keep the last indent. */
const MAX_INDENT = 8

/** The spans as one tree on the session's axis, with the picked moment's cursor. */
function Spans({ traces, total, cursor }: { traces: Trace[]; total: number; cursor: number }) {
  const i18n = useI18n()
  const { t } = i18n
  const { orgId, projectId } = useProjectParams()
  const search = useSearch({ strict: false }) as ScopeSearch
  const keep = carried(search)
  return (
    <div
      className="ss-wf"
      translate="no"
      style={
        {
          '--tick': `${(100 * (axisTicks(total)[1] ?? total)) / Math.max(1, total)}%`,
        } as CSSProperties
      }
    >
      <div className="ss-wax" aria-hidden="true">
        <span>{t('session.spans.span')}</span>
        <span className="ss-wd">{t('session.spans.duration')}</span>
        <span className="ss-ax">
          {axisTicks(total).map((ms) => (
            <span key={ms} style={{ left: at(ms, total) }}>
              {i18n.t('session.spans.tick', { s: ms / 1000 })}
            </span>
          ))}
        </span>
        <span />
      </div>
      {/* One tree on the session's axis (Console D; APS-393): the traces follow each other by
          start, each one's spans in tree order. A trace's root row links the trace, its summary
          in the link's title. */}
      {traces.flatMap((tr) => {
        const failed = tr.spans.filter((s) => s.failed).length
        const summary = t('session.spans.trace', {
          traced: tr.traceId ? 'yes' : 'no',
          id: shortId(tr.traceId),
          at: offset(i18n, tr.at),
          count: tr.spans.length,
          length: duration(i18n, tr.length),
          failed,
        })
        return tr.spans.map((s, i) => (
          <div
            key={`${tr.traceId || 'untraced'}-${s.id}`}
            className={s.depth > 0 ? 'ss-wr ch' : 'ss-wr'}
            style={{ '--d': Math.min(s.depth, MAX_INDENT) } as CSSProperties}
          >
            <span className="ss-wn" title={s.statusMessage || undefined}>
              {s.name}
              {s.failed ? <Pill tone="crash">{t('session.spans.failed')}</Pill> : null}
            </span>
            <span className="ss-wd">{duration(i18n, s.duration)}</span>
            <span className="ss-wt">
              <i
                className={s.failed ? 'ss-wb fail' : 'ss-wb'}
                style={{
                  left: at(tr.at + s.offset, total),
                  width: `max(2px, ${at(s.duration, total)})`,
                }}
              />
              <span className="ss-cur" style={{ left: at(cursor, total) }} />
            </span>
            <span className="ss-wl">
              {i === 0 && tr.traceId ? (
                <Link
                  className={LK}
                  to="/o/$orgId/p/$projectId/traces/$traceId"
                  params={{ orgId, projectId, traceId: tr.traceId }}
                  search={keep}
                  title={summary}
                >
                  {t('session.link.trace', { id: tr.traceId.slice(0, 4) })}
                </Link>
              ) : null}
            </span>
          </div>
        ))
      })}
    </div>
  )
}

const SEV_CLASS: Record<Severity, string> = {
  fatal: 'sev fatal',
  error: 'sev error',
  warn: 'sev warn',
  info: 'sev info',
  debug: 'sev debug',
  trace: 'sev debug',
}

/** How far back the Logs page reads (routes/search.ts VIEW_RANGES.logs). */
const LOGS_PAGE_DAYS = 7

/** The logs: the last LOG_WINDOW up to the end, then every log in place (nothing is lost). */
function Logs({
  logs,
  model,
  issues,
}: {
  logs: ReturnType<typeof sessionLogs>
  model: SessionModel
  issues: Issue[]
}) {
  const total = model.length
  const i18n = useI18n()
  const { t } = i18n
  const [all, setAll] = useState(false)
  const { orgId, projectId } = useProjectParams()
  const { sessionId } = useParams({ strict: false }) as { sessionId: string }
  const scope = useSearch({ strict: false }) as ScopeSearch
  const shown = logWindow(logs, all)
  const windowed = shown.length < logs.length
  const from = shown[0]?.at ?? 0
  // The Logs page reads the last 7 days: an older session's logs would not be there.
  const searchable = logs.length > 0 && Date.now() - model.startedAt < LOGS_PAGE_DAYS * 86_400_000
  return (
    <Section
      title={t('session.logs.title')}
      right={
        searchable ? (
          <Link
            className="lk"
            to="/o/$orgId/p/$projectId/logs"
            params={{ orgId, projectId }}
            search={{ ...(scope.app ? { app: scope.app } : {}), session: sessionId }}
          >
            {t('session.logs.search')}
          </Link>
        ) : undefined
      }
      meta={
        windowed
          ? t('session.logs.window', {
              shown: shown.length,
              count: logs.length,
              from: offset(i18n, Math.max(0, from)),
              to: offset(i18n, total),
            })
          : t('session.logs.count', { count: logs.length })
      }
    >
      {logs.length === 0 ? (
        <EmptyNote title={t('session.logs.none')} />
      ) : (
        <>
          <Table label={t('session.logs.title')} className="ss-logs">
            <thead>
              <tr>
                <th scope="col" className="f">
                  {t('session.col.t')}
                </th>
                <th scope="col">{t('session.col.severity')}</th>
                <th scope="col">{t('session.col.message')}</th>
                <th scope="col" className="last">
                  <span className="visually-hidden">{t('session.col.links')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {shown.map((l) => (
                <tr key={l.id}>
                  <td className="f">{offset(i18n, l.at)}</td>
                  <td>
                    <span className={SEV_CLASS[l.severity]}>
                      {t(`session.severity.${l.severity}` as MessageId)}
                    </span>
                  </td>
                  <td className="wrap" translate="no">
                    {l.attributes.length > 0 ? (
                      <details>
                        <summary>
                          <LogLine name={l.name} body={l.body} />
                        </summary>
                        <dl className="kv ss-lkv">
                          {l.attributes.map(([k, v]) => (
                            <Pair key={k} k={k} v={v} />
                          ))}
                        </dl>
                      </details>
                    ) : (
                      <LogLine name={l.name} body={l.body} />
                    )}
                  </td>
                  <td className="last">
                    <Links links={logLinks(l, model, issues)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <TableFoot>
            <span>{t('session.logs.foot')}</span>
            {logs.length > LOG_WINDOW ? (
              <Btn size="sm" variant="ghost" onClick={() => setAll(!all)}>
                {all
                  ? t('session.logs.last', { count: LOG_WINDOW })
                  : t('session.logs.all', { count: logs.length })}
              </Btn>
            ) : null}
          </TableFoot>
        </>
      )}
    </Section>
  )
}

/** A log's links: its trace, and its issue (logIssue). */
function logLinks(
  l: ReturnType<typeof sessionLogs>[number],
  model: SessionModel,
  issues: Issue[],
): EventLink[] {
  const issue = logIssue(l, model, issues)
  return [
    ...(l.traceId ? [{ kind: 'trace' as const, traceId: l.traceId }] : []),
    ...(issue ? [{ kind: 'issue' as const, id: issue.id }] : []),
  ]
}

function LogLine({ name, body }: { name: string; body: string }) {
  return (
    <>
      {name ? <span className="ss-ln">{name} </span> : null}
      {body}
    </>
  )
}

function Pair({ k, v }: { k: string; v: string }) {
  return (
    <>
      <dt>{k}</dt>
      <dd>{v}</dd>
    </>
  )
}

export function SessionPage() {
  const i18n = useI18n()
  const { t, date, relative } = i18n
  const { orgId, projectId } = useProjectParams()
  const { sessionId } = useParams({ strict: false }) as { sessionId: string }
  const search = useSearch({ strict: false }) as ScopeSearch
  const keep = carried(search)
  const session = useQuery(sessionQuery(projectId, sessionId))
  const [pickedId, setPickedId] = useState<string>()
  const [allEvents, setAllEvents] = useState(false)
  const replay = useReplay(projectId, sessionId)
  const [replayAt, setReplayAt] = useState<number>()
  const { toast, show } = useToast()
  // Device data (D111) asks this session's device; the sandbox has no organization to ask from.
  const sandbox = !!sandboxFor(projectId)
  const org = useQuery({ ...orgQuery(orgId), enabled: !sandbox })
  const canEdit = org.data ? org.data.role === 'owner' || org.data.role === 'admin' : undefined

  const crumb = (
    <>
      <Link to="/o/$orgId/p/$projectId/sessions" params={{ orgId, projectId }} search={keep}>
        {t('session.crumb')}
      </Link>
      {' / '}
      <span translate="no">{shortId(sessionId)}</span>
    </>
  )
  if (session.isPending)
    return (
      <main id="main" className="pg ss-pg">
        <PageHead crumb={crumb} title={shortId(sessionId)} variant="mono" />
        <Loading />
      </main>
    )
  if (session.isError)
    return (
      <main id="main" className="pg ss-pg">
        <PageHead crumb={crumb} title={shortId(sessionId)} variant="mono" />
        <ErrorState error={session.error} retry={() => session.refetch()} />
      </main>
    )

  const data = session.data
  const model = sessionModel(data)
  const summary = model.summary
  if (!summary && model.events.length === 0)
    return (
      <main id="main" className="pg ss-pg">
        <PageHead crumb={crumb} title={shortId(sessionId)} variant="mono" />
        <EmptyNote title={t('session.empty')}>{t('session.empty.body')}</EmptyNote>
      </main>
    )

  const issues: Issue[] = data.issues ?? []
  const fractions = orbitFractions(model)
  const picked = model.events.find((e) => e.id === pickedId) ?? model.ephemeris[model.periapsis]
  // The orbit shows the ephemeris: an event off it puts the dot on the last one before it.
  const orbitIndex = picked
    ? Math.max(
        0,
        model.ephemeris.reduce((n, e, i) => (e.at <= picked.at ? i : n), 0),
      )
    : model.periapsis
  const exact = picked ? model.ephemeris.indexOf(picked) : -1
  const selected = exact >= 0 ? exact : orbitIndex
  const cursor = replayAt !== undefined ? replayAt - model.startedAt : (picked?.at ?? 0)
  const span = model.ephemeris.at(-1)?.at ?? 0
  const hangs = model.ephemeris
    .map((e, i) => ({ e, i }))
    .filter(({ e }) => e.signal === 'hang')
    .map(({ e, i }) => ({
      fraction: fractions[i] ?? 0,
      endFraction: span > 0 ? Math.min(1, (e.at + e.duration) / span) : 1,
    }))
  // The events a replay viewer jumps to: crashes, errors, hangs and screens.
  const markers: ReplayMarker[] = model.events
    .filter((e) => ['crash', 'error', 'hang', 'screen'].includes(e.signal))
    .map((e) => ({
      id: e.id,
      at: model.startedAt + e.at,
      name: e.name,
      tone: e.tone === 'crash' ? 'crash' : e.tone === 'warn' ? 'warn' : 'neutral',
    }))
  const pick = (e: TimelineEvent) => {
    setPickedId(e.id)
    setReplayAt(model.startedAt + e.at)
  }
  const rows = allEvents ? model.events : model.ephemeris
  const logs = sessionLogs(data, model.startedAt)
  const traces = sessionTraces(data, model.startedAt)
  const spanCount = traces.reduce((n, tr) => n + tr.spans.length, 0)
  const primary = headIssue(issues)
  // The session's feature flags (step 3.6), one line per flag (its variants together when it
  // changed in the session).
  const variantsByFlag = new Map<string, string[]>()
  for (const f of data.flags ?? [])
    variantsByFlag.set(f.name, [...(variantsByFlag.get(f.name) ?? []), f.variant])
  const attrs: [string, string][] = (
    summary
      ? [
          [RESOURCE.sessionId, data.session_id],
          [RESOURCE.serviceVersion, summary.release],
          [RESOURCE.buildId, summary.build],
          [RESOURCE.deviceModel, summary.device_model],
          [RESOURCE.osName, summary.os_name],
          [RESOURCE.osVersion, summary.os_version],
          [RESOURCE.installationId, summary.installation_id],
        ]
      : [[RESOURCE.sessionId, data.session_id]]
  ).filter((pair): pair is [string, string] => Boolean(pair[1]))

  return (
    <main id="main" className="pg ss-pg">
      <PageHead
        crumb={crumb}
        title={<span translate="no">{shortId(sessionId)}</span>}
        variant="mono"
        meta={
          <>
            {findingText(i18n, model, summary?.error_count ?? 0)}
            <br />
            <span translate="no">
              {[summary?.device_model, summary ? `${summary.os_name} ${summary.os_version}` : '']
                .map((x) => x?.trim())
                .filter(Boolean)
                .join(' · ')}
              {summary?.release ? (
                <>
                  {' · '}
                  <Link
                    className="ul"
                    to="/o/$orgId/p/$projectId/releases/$release"
                    params={{ orgId, projectId, release: summary.release }}
                    search={{ ...keep, release: undefined }}
                  >
                    {summary.release}
                  </Link>
                </>
              ) : null}
            </span>
            {` · ${t('session.when', {
              date: date(model.startedAt, {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hourCycle: 'h23',
              }),
              ago: relative(new Date(model.startedAt)),
            })}`}
            {summary ? (
              <ReleasedBy
                orgId={orgId}
                projectId={projectId}
                appId={summary.app_id}
                triggers={summary.triggers}
                sandbox={sandbox}
              />
            ) : null}
          </>
        }
        actions={
          <>
            {primary ? (
              <Link
                className={btnClass('solid')}
                to="/o/$orgId/p/$projectId/issues/$issueId"
                params={{ orgId, projectId, issueId: primary.id }}
                search={keep}
              >
                {t('session.openIssue')} <code translate="no">{shortId(primary.id, 8, 4)}</code>
              </Link>
            ) : null}
            {!sandbox && summary?.installation_id ? (
              <DeviceData
                orgId={orgId}
                projectId={projectId}
                install={summary.installation_id}
                canEdit={canEdit}
                label={t('device.button')}
              />
            ) : null}
            <Btn
              onClick={async () => {
                if (await copyText(window.location.href)) show(t('session.copied'))
              }}
            >
              {t('session.copyLink')}
            </Btn>
          </>
        }
      />
      {toast}
      <div className="grid2">
        <div className="stack">
          <Section
            title={t('session.events.title')}
            meta={t('session.events.count', { count: model.events.length })}
          >
            <div className="orbit ss-orbit">
              <Orbit
                ticks={fractions.map((f) => ({ fraction: f }))}
                hangs={hangs}
                periapsis={model.periapsis}
                selected={selected}
                deviceLabel={t('session.device')}
                {...(picked
                  ? {
                      centerTitle: offset(i18n, Math.max(0, picked.at)),
                      centerSub: [picked.name, picked.sub].filter(Boolean).join(' · '),
                    }
                  : {})}
              />
            </div>
            <Table label={t('session.events.title')} className="ss-eph" rowKeys>
              <thead>
                <tr>
                  <th scope="col" className="f">
                    {t('session.col.no')}
                  </th>
                  <th scope="col">{t('session.col.t')}</th>
                  <th scope="col">{t('session.col.signal')}</th>
                  <th scope="col" className="ss-det">
                    {t('session.col.detail')}
                  </th>
                  <th scope="col">{t('session.col.value')}</th>
                  <th scope="col" className="last ss-lks">
                    {t('session.col.links')}
                  </th>
                </tr>
              </thead>
              <tbody
                onFocus={(e) => {
                  const tr = (e.target as HTMLElement).closest('tr')
                  const i = tr ? [...(tr.parentElement?.children ?? [])].indexOf(tr) : -1
                  const ev = i >= 0 ? rows[i] : undefined
                  if (ev && ev.id !== picked?.id) pick(ev)
                }}
              >
                {rows.map((e, i) => {
                  const value = valueText(i18n, e)
                  const tone = pillTone(e)
                  return (
                    <HovRow
                      key={e.id}
                      cur={e.id === picked?.id}
                      onOpen={() => pick(e)}
                      label={`${i + 1} ${e.name}`}
                    >
                      <td className="f ss-no">{i + 1}</td>
                      <td>{offset(i18n, e.at)}</td>
                      <td className="ss-sig" translate="no">
                        {e.name}
                        {e.sub ? <span className="t2">{e.sub}</span> : null}
                        {e.phase === 'launch' ? (
                          <>
                            {' '}
                            <Tag title={t('session.hang.launch.title')}>
                              <span translate="yes">{t('session.hang.launch')}</span>
                            </Tag>
                          </>
                        ) : null}
                      </td>
                      <td className="ss-det" translate="no">
                        {e.sub || '—'}
                      </td>
                      <td>{value ? <Pill {...(tone ? { tone } : {})}>{value}</Pill> : null}</td>
                      <td className="last ss-lks">
                        <Links links={eventLinks(e, issues)} />
                      </td>
                    </HovRow>
                  )
                })}
              </tbody>
            </Table>
            <TableFoot>
              <span>{model.outcome === OUTCOME.crashed ? t('session.events.foot') : ''}</span>
              {model.events.length > model.ephemeris.length ? (
                <Btn size="sm" variant="ghost" onClick={() => setAllEvents(!allEvents)}>
                  {allEvents
                    ? t('session.events.notable', { count: model.ephemeris.length })
                    : t('session.events.all', { count: model.events.length })}
                </Btn>
              ) : null}
            </TableFoot>
          </Section>
          <Section
            title={t('session.lanes.title')}
            meta={t('session.lanes.range', { end: duration(i18n, model.length) })}
          >
            <Lanes model={model} cursor={cursor} />
          </Section>
          <Section
            title={t('session.spans.title')}
            meta={t('session.spans.count', { count: spanCount })}
          >
            {traces.length === 0 ? (
              <EmptyNote title={t('session.spans.none')} />
            ) : (
              <Spans traces={traces} total={model.length} cursor={cursor} />
            )}
          </Section>
          <Logs logs={logs} model={model} issues={issues} />
        </div>
        <div className="stack">
          {replay.timeline !== undefined ? (
            <Section
              title={t('replay.title')}
              meta={t('session.replay.segments', { count: replay.segments.length })}
            >
              <div className="win ss-rp">
                <div className="win-bar">
                  <b>{t('session.replay.wireframe')}</b>
                  <span>{t('session.replay.masked')}</span>
                  {picked ? (
                    <span className="r" translate="no">
                      {`#${model.events.indexOf(picked) + 1} ${picked.name}`}
                    </span>
                  ) : null}
                </div>
                <ReplayBoundary fallback={<p className="note">{t('replay.failed')}</p>}>
                  {replay.timeline ? (
                    <ReplayPlayer
                      timeline={replay.timeline}
                      sessionStart={model.startedAt}
                      at={replayAt ?? replay.timeline.startAt}
                      onSeek={setReplayAt}
                      markers={markers}
                      pending={replay.pending}
                    />
                  ) : (
                    <p className="note">{t('replay.failed')}</p>
                  )}
                </ReplayBoundary>
                <p className="note">{t('session.replay.note')}</p>
              </div>
            </Section>
          ) : null}
          {issues.length > 0 ? (
            <Section
              title={t('session.issues.title')}
              meta={t('session.issues.count', { count: issues.length })}
            >
              <ul className="rl ss-issues">
                {issues.map((i) => (
                  <li key={i.id}>
                    <span>
                      <Kind k={i.kind} /> <span translate="no">{i.title || i.type}</span>
                    </span>
                    <Link
                      className={LK}
                      to="/o/$orgId/p/$projectId/issues/$issueId"
                      params={{ orgId, projectId, issueId: i.id }}
                      search={keep}
                      translate="no"
                    >
                      {shortId(i.id, 8, 4)}
                    </Link>
                  </li>
                ))}
              </ul>
            </Section>
          ) : null}
          <Section title={t('session.attributes.title')} meta={t('session.attributes.names')}>
            <dl className="kv ss-kv" translate="no">
              {attrs.map(([k, v]) => (
                <Pair key={k} k={k} v={v} />
              ))}
            </dl>
          </Section>
          {variantsByFlag.size > 0 ? (
            <Section
              title={t('session.flags.title')}
              meta={t('session.flags.count', { count: variantsByFlag.size })}
            >
              <ul className="rl ss-flags">
                {[...variantsByFlag].map(([name, variants]) => (
                  <li key={name}>
                    <code translate="no">{`${FLAG_ATTR_PREFIX}${name}`}</code>
                    <span>
                      <span translate="no">{variants.join(', ')}</span>
                      {variants.length === 1 ? (
                        <Link
                          className={LK}
                          to="/o/$orgId/p/$projectId/sessions"
                          params={{ orgId, projectId }}
                          search={{ ...keep, flag: name, variant: variants[0] }}
                        >
                          {t('session.flags.sessions')}
                        </Link>
                      ) : null}
                    </span>
                  </li>
                ))}
              </ul>
            </Section>
          ) : null}
        </div>
      </div>
    </main>
  )
}

/** The built-in triggers that are failures, drawn in the crash tone. */
const FAILURE_TRIGGERS = new Set(['crash', 'error', 'anr'])

/**
 * What released the session's held records (D114, spec 3.4 `apsio.session.triggers`): a rule by
 * its name, linking to it on Signals, a built-in trigger by its word. Nothing when none fired.
 */
function ReleasedBy({
  orgId,
  projectId,
  appId,
  triggers,
  sandbox,
}: {
  orgId: string
  projectId: string
  appId: string
  triggers: string[]
  sandbox: boolean
}) {
  const { t, locale } = useI18n()
  const hasRule = triggers.some((x) => x.startsWith('rule:'))
  const policy = useQuery({
    ...instrumentationQuery(orgId, projectId, appId),
    enabled: hasRule && !sandbox,
  })
  if (triggers.length === 0) return null
  const rules = policy.data?.rules ?? []
  return (
    <>
      {` · ${t('session.releasedBy')} `}
      {triggers.map((x, i) => {
        const rule = x.startsWith('rule:')
        const label = triggerLabel(t, x, rules)
        return (
          <Fragment key={x}>
            {i > 0 ? ' · ' : null}
            {rule && !sandbox ? (
              <Link
                className="ul"
                to="/o/$orgId/p/$projectId/signals/rules/$ruleId"
                params={{ orgId, projectId, ruleId: x.slice('rule:'.length) }}
                search={{ app: appId }}
              >
                {label}
              </Link>
            ) : rule ? (
              label
            ) : (
              // A built-in's word in lower case, as a kind ("crash"); an acronym stays (ANR).
              <span className={FAILURE_TRIGGERS.has(x) ? 'sx-trig bad' : 'sx-trig'}>
                {label === label.toUpperCase() ? label : label.toLocaleLowerCase(locale)}
              </span>
            )}{' '}
            <span className="dim">{t(rule ? 'session.byRule' : 'session.byTrigger')}</span>
          </Fragment>
        )
      })}
    </>
  )
}
